/**
 * Tiny stale-while-revalidate store behind the API services. Reads return the
 * last known value synchronously so a view can paint immediately on a repeat
 * visit; the service still fetches and overwrites it with fresh data.
 *
 * Memory is the first layer. Public, non-user-specific entries (`persist`) are
 * also mirrored to localStorage so they survive a reload. Everything is
 * dropped when the session changes (login/logout) so one account's data is
 * never shown to the next.
 */
const PREFIX = 'recipease-cache:';
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

interface Entry<T> {
	value: T;
	at: number;
}

const memory = new Map<string, Entry<unknown>>();

export function cacheGet<T>(key: string): T | undefined {
	const hit = memory.get(key) as Entry<T> | undefined;
	if (hit) return hit.value;
	try {
		const raw = window.localStorage.getItem(PREFIX + key);
		if (!raw) return undefined;
		const entry = JSON.parse(raw) as Entry<T>;
		if (Date.now() - entry.at > MAX_AGE_MS) {
			window.localStorage.removeItem(PREFIX + key);
			return undefined;
		}
		memory.set(key, entry);
		return entry.value;
	} catch {
		return undefined;
	}
}

export function cacheSet<T>(key: string, value: T, persist = false): void {
	const entry: Entry<T> = { value, at: Date.now() };
	memory.set(key, entry);
	if (!persist) return;
	try {
		window.localStorage.setItem(PREFIX + key, JSON.stringify(entry));
	} catch {
		// storage full or unavailable — memory copy still serves this tab
	}
}

/** Drop every entry whose key starts with `prefix` (all of them by default). */
export function cacheClear(prefix = ''): void {
	for (const key of [...memory.keys()]) {
		if (key.startsWith(prefix)) memory.delete(key);
	}
	try {
		for (const key of Object.keys(window.localStorage)) {
			if (key.startsWith(PREFIX + prefix)) window.localStorage.removeItem(key);
		}
	} catch {
		// ignore
	}
}
